/**
 * Thin wrappers around @farcaster/miniapp-sdk for in-client ready, compose, wallet, and add.
 * Safe to call from a normal browser — helpers no-op when not in a Mini App host.
 */

let readyPromise: Promise<boolean> | null = null;
let miniAppPromise: Promise<boolean> | null = null;

async function loadSdk() {
  const mod = await import('@farcaster/miniapp-sdk');
  return mod.sdk;
}

/** True when the page is running inside a Farcaster Mini App WebView/iframe. */
export async function isInFarcasterMiniApp(): Promise<boolean> {
  if (!miniAppPromise) {
    miniAppPromise = (async () => {
      try {
        const sdk = await loadSdk();
        return Boolean(await sdk.isInMiniApp());
      } catch {
        return false;
      }
    })();
  }
  return miniAppPromise;
}

/**
 * Dismiss the host splash screen. Idempotent; call once on app boot.
 * Returns whether we successfully signaled ready inside a Mini App.
 */
export function ensureFarcasterMiniAppReady(): Promise<boolean> {
  if (!readyPromise) {
    readyPromise = (async () => {
      try {
        const sdk = await loadSdk();
        if (!(await sdk.isInMiniApp())) return false;
        await sdk.actions.ready();
        return true;
      } catch {
        return false;
      }
    })();
  }
  return readyPromise;
}

export type ComposeCastOpts = {
  text: string;
  embeds?: string[];
};

/**
 * Open the host compose sheet when in a Mini App. Returns true if compose opened.
 */
export async function tryComposeCast(opts: ComposeCastOpts): Promise<boolean> {
  try {
    const sdk = await loadSdk();
    if (!(await sdk.isInMiniApp())) return false;
    const embeds = (opts.embeds || []).filter(Boolean).slice(0, 2) as [] | [string] | [string, string];
    await sdk.actions.composeCast({
      text: opts.text,
      embeds,
    });
    return true;
  } catch {
    return false;
  }
}

/** EIP-1193 provider from the Mini App host, or null on the open web. */
export async function getFarcasterEthereumProvider(): Promise<unknown> {
  try {
    const sdk = await loadSdk();
    if (!(await sdk.isInMiniApp())) return null;
    return (await sdk.wallet.getEthereumProvider()) ?? null;
  } catch {
    return null;
  }
}

export async function hapticSuccess(): Promise<void> {
  try {
    const sdk = await loadSdk();
    if (!(await sdk.isInMiniApp())) return;
    const haptics = (
      sdk as {
        haptics?: { notificationOccurred?: (type: 'success' | 'warning' | 'error') => Promise<void> };
      }
    ).haptics;
    await haptics?.notificationOccurred?.('success');
  } catch {
    // Host may not support haptics.
  }
}

const ADD_MINIAPP_KEY = 'takni:miniapp:add-prompted';

/**
 * Ask the host to save this Mini App. Once per browser. No-op outside a Mini App.
 */
export async function promptAddMiniAppOnce(): Promise<void> {
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem(ADD_MINIAPP_KEY)) return;
    const sdk = await loadSdk();
    if (!(await sdk.isInMiniApp())) return;
    if (typeof localStorage !== 'undefined') localStorage.setItem(ADD_MINIAPP_KEY, '1');
    const add = (sdk.actions as { addMiniApp?: () => Promise<unknown> }).addMiniApp;
    if (typeof add === 'function') await add();
  } catch {
    // Host may not support addMiniApp.
  }
}
